import * as React from 'react'
import { FormattedMessage, defineMessages, MessageDescriptor, useIntl } from 'react-intl'
import { validatePhoneNumber, normalizePhoneNumber } from '../../../../lib/utils'
import { useFormState } from 'react-use-form-state'
import { Text, getValidity } from '../../../../components/form'
import Button from '../../../../components/button'
import FormCard from '../../../../components/form-card'
import styles from './contactCard.module.scss'

interface IContactCardProps {
  ctaText?: MessageDescriptor
  error?: string | React.ReactNode
  disabled?: boolean
  labelText?: MessageDescriptor
  includeBank?: boolean
  initialValues?: {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phoneNumber?: string | null
    bank?: string | null
  }
  handleSuccess?: () => void
  handleFail?: () => void
  onComplete: (firstName: string, lastName: string, email: string, phone: string, customBank?: string) => Promise<null>
  clearError: () => void
}

interface IFormValues {
  firstName: string
  lastName: string
  email: string
  phone: string
  bank?: string
}

const messages = defineMessages({
  errorPhone: {
    id: 'sync-bank.components.contact-card.error-phone',
    defaultMessage: 'Du måste ange ett korrekt mobilnummer',
  },
  errorFirstName: {
    id: 'sync-bank.components.contact-card.error-first-name',
    defaultMessage: 'Du måste ange ditt förnamn',
  },
  errorLastName: {
    id: 'sync-bank.components.contact-card.error-last-name',
    defaultMessage: 'Du måste ange ditt efternamn',
  },
  errorEmail: {
    id: 'sync-bank.components.contact-card.error-email',
    defaultMessage: 'Du måste ange en epost-adress',
  },
  errorBank: {
    id: 'sync-bank.components.contact-card.error-bank',
    defaultMessage: 'Du måste ange en bank',
  },
})

function ContactCard({
  onComplete,
  clearError,
  error,
  disabled,
  includeBank,
  handleSuccess,
  handleFail,
  initialValues,
  ctaText,
  labelText,
}: IContactCardProps) {
  const [formState, input] = useFormState<IFormValues>(
    {
      firstName: (initialValues && initialValues.firstName) || '',
      lastName: (initialValues && initialValues.lastName) || '',
      email: (initialValues && initialValues.email) || '',
      phone: (initialValues && initialValues.phoneNumber) || '',
      bank: (initialValues && initialValues.bank) || '',
    } as IFormValues,
    {
      withIds: true,
    }
  )

  // Use intl HOC
  const intl = useIntl()

  const { firstName, lastName, email, phone, bank } = formState.values
  const errorMessages = {
    firstName: intl.formatMessage(messages.errorFirstName),
    lastName: intl.formatMessage(messages.errorLastName),
    email: intl.formatMessage(messages.errorEmail),
    phone: intl.formatMessage(messages.errorPhone),
    bank: intl.formatMessage(messages.errorBank),
  }

  const inputs = [
    {
      node: (
        <Text
          errorCollapse="dynamic"
          errorMessage={errorMessages.firstName}
          hideIcon
          input={input}
          label="Förnamn"
          name="firstName"
          required
          valid={getValidity(formState, 'firstName')}
          validate={(value: string) => (value ? true : errorMessages.firstName)}
          validateOnBlur
        />
      ),
      key: 1,
    },
    {
      node: (
        <Text
          errorCollapse="dynamic"
          errorMessage={errorMessages.lastName}
          hideIcon
          input={input}
          key={2}
          label="Efternamn"
          name="lastName"
          required
          valid={getValidity(formState, 'lastName')}
          validate={(value: string) => (value ? true : errorMessages.lastName)}
          validateOnBlur
        />
      ),
      key: 2,
    },
    {
      node: (
        <Text
          errorCollapse="dynamic"
          errorMessage={errorMessages.email}
          hideIcon
          input={input}
          key={3}
          label="Epost-adress"
          name="email"
          required
          valid={getValidity(formState, 'email')}
          validate={(value: string) => (value ? true : errorMessages.email)}
          validateOnBlur
        />
      ),
      key: 3,
    },
    {
      node: (
        <Text
          errorCollapse="dynamic"
          errorMessage={errorMessages.phone}
          hideIcon
          input={input}
          key={4}
          label="Mobilnummer"
          name="phone"
          required
          valid={getValidity(formState, 'phone')}
          validate={(value: string) => (validatePhoneNumber(normalizePhoneNumber(value)) ? true : errorMessages.phone)}
          validateOnBlur
        />
      ),
      key: 4,
    },
  ]

  if (includeBank) {
    inputs.push({
      node: (
        <Text
          errorCollapse="dynamic"
          errorMessage={errorMessages.bank}
          hideIcon
          input={input}
          key={4}
          label="Bank"
          name="bank"
          required
          valid={getValidity(formState, 'bank')}
          validate={(value: string) => (value ? true : errorMessages.bank)}
          validateOnBlur
        />
      ),
      key: 5,
    })
  }

  return (
    <FormCard
      button={
        <Button block disabled={disabled} type="submit" variant="success">
          {ctaText ? (
            <FormattedMessage {...ctaText} />
          ) : (
            <FormattedMessage
              defaultMessage="Skicka mig information om Dooer"
              id="sync-bank.components.contact-card.button"
            />
          )}
        </Button>
      }
      className={styles.contactCard}
      col={2}
      error={error}
      header={
        labelText ? (
          <FormattedMessage {...labelText} />
        ) : (
          <FormattedMessage
            defaultMessage="Håll mig uppdaterad om vad som händer på Dooer"
            id="sync-bank.components.contact-card.heading"
          />
        )
      }
      inputs={inputs}
      onSubmit={(e) => {
        e.preventDefault()
        onComplete(firstName, lastName, email, phone, bank)
          .then(() => {
            formState.reset()
            formState.clear()
            clearError()
            if (handleSuccess) handleSuccess()
          })
          .catch(() => {
            if (handleFail) handleFail()
          })
      }}
      style={{ marginBottom: '4rem' }}
    />
  )
}

export default ContactCard
